import { useState } from 'react'
import { useMetaMask } from './hooks/use-metamask'
import { useVocdoniAccount } from './hooks/use-vocdoni'
import { useVocdoni } from './contexts/vocdoni-context'
import { CensusCreator } from './components/census-creator'
import { ElectionCreator } from './components/election-creator'
import { Election, PlainCensus } from '@vocdoni/sdk'
import './App.css'

// Add these styles to App.css
const explorerLinkStyle = {
  display: 'inline-flex',
  alignItems: 'center',
  color: '#0066cc',
  textDecoration: 'none',
  cursor: 'pointer'
}

const iconStyle = {
  marginLeft: '8px',
  width: '16px',
  height: '16px'
}

interface ElectionParams {
  title: string
  description: string
  durationDays: number
  minParticipation: number
  minSupport: number
  maxVoteOverwrites: number
  anonymous: boolean
  secretUntilTheEnd: boolean
}

const AUTHORIZED_ACCOUNT = '0x0CFf75f5CdB7200B4afa9d72d08f615F4E6aCD22'

export default function App() {
  const { account, connect, isConnecting, error: metamaskError } = useMetaMask()
  const { 
    account: vocdoniAccount, 
    createAccount, 
    isCreating, 
    error: vocdoniError 
  } = useVocdoniAccount()
  const { client } = useVocdoni()

  const [censusComplete, setCensusComplete] = useState(false)
  const [censusAddresses, setCensusAddresses] = useState<string[]>([])
  const [electionId, setElectionId] = useState<string>('')

  const isAuthorized = account?.toLowerCase() === AUTHORIZED_ACCOUNT.toLowerCase()

  const handleCreateAccount = async () => {
    if (account && isAuthorized) {
      await createAccount()
    }
  }

  const handleCensusComplete = (addresses: string[]) => {
    setCensusAddresses(addresses)
    setCensusComplete(true)
  }

  const handleCreateElection = async (params: ElectionParams) => {
    try {
      if (!vocdoniAccount || !client) {
        throw new Error('Vocdoni account or client not initialized')
      }

      // Create a new census
      const census = new PlainCensus()
      for (const address of censusAddresses) {
        census.add(address)
      }

      // Calculate end date based on duration days
      const endDate = new Date()
      endDate.setDate(endDate.getDate() + params.durationDays)

      // Create the election
      const election = Election.from({
        title: params.title,
        description: params.description,
        header: "https://source.unsplash.com/random",
        streamUri: "https://source.unsplash.com/random",
        endDate: endDate.getTime(),
        census
      })

      // Add a simple Yes/No question
      election.addQuestion(
        'Do you approve?',
        'Cast your vote',
        [
          { title: 'Yes', value: 0 },
          { title: 'No', value: 1 }
        ]
      )

      // Create the election on Vocdoni
      const newElectionId = await client.createElection(election)
      setElectionId(newElectionId)
    } catch (err) {
      console.error('Failed to create election:', err)
    }
  }

  const ExternalLinkIcon = () => (
    <svg 
      style={iconStyle} 
      fill="none" 
      stroke="currentColor" 
      viewBox="0 0 24 24"
    >
      <path 
        strokeLinecap="round" 
        strokeLinejoin="round" 
        strokeWidth={2} 
        d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14"
      />
    </svg>
  )

  return (
    <div style={{ padding: '20px' }}>
      <h1>Robs Rules DAO</h1>
      
      {!account ? (
        <button 
          onClick={connect}
          disabled={isConnecting}
          className="connect-button"
        >
          {isConnecting ? 'Connecting...' : 'Connect MetaMask'}
        </button>
      ) : !isAuthorized ? (
        <div>
          <p style={{ color: 'red' }}>Account is not authorized to participate</p>
          <p>Connected Account: {account}</p>
        </div>
      ) : !vocdoniAccount ? (
        <div>
          <p>Connected Account: {account}</p>
          <button
            onClick={handleCreateAccount}
            disabled={isCreating}
            className="connect-button"
          >
            {isCreating ? 'Creating Account...' : 'Create Vocdoni Account'}
          </button>
        </div>
      ) : (
        <div>
          <p>Connected Account: {account}</p>
          <p>Vocdoni Account Created ✓</p>
          <p>Vocdoni Account Address: {vocdoniAccount.address}</p>
          <p>Vocdoni Account Balance: {vocdoniAccount.balance}</p>
          
          {!censusComplete ? (
            <CensusCreator 
              onAddressesComplete={handleCensusComplete}
              initialAddress={account}
            />
          ) : !electionId ? (
            <ElectionCreator onCreateElection={handleCreateElection} />
          ) : (
            <div>
              <h2>Election Created!</h2>
              <p>
                Election ID:{' '}
                <a
                  href={`https://stg.explorer.vote/processes/show/#/${electionId}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  style={explorerLinkStyle}
                >
                  {electionId}
                  <ExternalLinkIcon />
                </a>
              </p>
              <p>Census Size: {censusAddresses.length} addresses</p>
            </div>
          )}
        </div>
      )}

      {(metamaskError || vocdoniError) && (
        <p style={{ color: 'red' }}>{metamaskError || vocdoniError}</p>
      )}
    </div>
  )
}
